// DataPanel — the local-first data section, pure.
//
// The desktop app's whole truth is ONE events file on the author's computer
// (events are the only truth — memory feedback_events_are_truth). This panel
// makes that tangible: where the file is (mono — machine data, a trust
// signal), how big it is, and a native reveal affordance whose label follows
// the OS register (Finder / Explorer / file manager — platform arrives from
// the bridge, never UA-sniffed). Honesty over polish: the beta note says
// plainly that the data format may still change, so an author who cares can
// keep a copy before updating.
//
// The size label arrives PRE-FORMATTED (`sizeLabel`) — the consumer formats
// bytes with its locale; this panel is a pure view and renders what it is
// given.
//
// When there is no DesktopBridge (`location.type === "web_build"`) the section
// renders an honest note instead of a dead path row — no false affordances.
//
// Composition: SettingsSection + SettingsRow + Mono (path/size) + Button
// (reveal). Register: near-monochrome; nothing here is a status, so no
// semantic colour is spent.
//
// Pure/props-driven: the location state arrives as a discriminated sum and is
// matched totally; the one action (reveal) reports UP.

import { Trans, useLingui } from "@lingui/react/macro";
import type { DesktopPlatform } from "../../../app/desktopBridge.js";
import { Button } from "../../Button/Button.js";
import { HelpHint } from "../../HelpHint/HelpHint.js";
import { Mono } from "../../Mono/Mono.js";
import { Text } from "../../Text/Text.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";
import type { DataLocationLoaded, DataLocationState } from "../types.js";
import "./DataPanel.css";

export type DataPanelProps = {
    readonly location: DataLocationState;
    // Ask the OS to reveal the events file; the consumer calls the bridge.
    readonly onRevealEventsFile: () => void;
};

const LoadedRows = ({
    location,
    onRevealEventsFile,
}: {
    readonly location: DataLocationLoaded;
    readonly onRevealEventsFile: () => void;
}) => {
    const { t } = useLingui();

    // Total over the platform union — a fourth OS fails to compile here.
    const revealLabels: Record<DesktopPlatform, string> = {
        mac: t`Show in Finder`,
        windows: t`Show in Explorer`,
        linux: t`Show in file manager`,
    };

    return (
        <>
            <SettingsRow
                label={
                    <span className="bilda-data-panel__label-hint">
                        {t`Events file`}
                        {/* H13 (inline-help.md): the row's own description
                            already says WHAT the file is; the hint adds the
                            back-it-up-and-you-have-everything framing — no
                            htmlFor on this row, so the caption renders as a
                            plain span, not a <label>, and nesting this button
                            is safe. */}
                        <HelpHint label={t`Explanation: events file`}>
                            <Trans>
                                Every change you make is saved as an event in
                                this file – the file is your entire work. Back
                                it up and you have everything.
                            </Trans>
                        </HelpHint>
                    </span>
                }
                description={t`Every change you make is appended here — this file is your work.`}
                control={
                    <div className="bilda-data-panel__path-and-size">
                        <Mono as="code" className="bilda-data-panel__path">
                            {location.eventsPath}
                        </Mono>
                        <Mono>{location.sizeLabel}</Mono>
                    </div>
                }
            />

            <SettingsRow
                label={t`Location`}
                description={t`Open the file's place on disk.`}
                control={
                    <Button variant="secondary" onClick={onRevealEventsFile}>
                        {revealLabels[location.platform]}
                    </Button>
                }
            />
        </>
    );
};

export const DataPanel = ({ location, onRevealEventsFile }: DataPanelProps) => {
    const { t } = useLingui();

    // location.type → body: an exhaustive match via a total object literal
    // would eagerly render all three, so a switch-shaped conditional chain
    // over the discriminant does it — every variant named, no default branch.
    const body =
        location.type === "web_build" ? (
            <Text tone="dim" size="sm" as="p">
                {t`In the web build your work is stored by the server, so there is no local file to show. The desktop app keeps everything in one events file on your computer.`}
            </Text>
        ) : location.type === "loading" ? (
            <Text tone="dim" size="sm" as="p">
                {t`Reading file details…`}
            </Text>
        ) : (
            <LoadedRows
                location={location}
                onRevealEventsFile={onRevealEventsFile}
            />
        );

    return (
        <SettingsSection
            title={t`Data`}
            description={t`Local-first: your work lives on this computer.`}
        >
            {body}
            <Text tone="dim" size="xs" as="p">
                {t`Beta: the data format may still change between updates. If the work matters, keep a copy of the events file before updating.`}
            </Text>
        </SettingsSection>
    );
};
